// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Close from "@flip/XDL/Icons/Close";
import RemoveAll from "@flip/XDL/Icons/RemoveAll";
import { buttonTransparent } from "@flip/XDL/PreStyle/button";
import { xBaseTheme } from "@flip/XDL/types";
import { color, ellipsis, styledScrollbar, xScreen } from "@flip/XDL/Utilities";
import { IconWrapper } from "@insite/mobius/Icon";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { css } from "styled-components";

const MODAL_DESKTOP_PADDING = 20;
const MODAL_MOBILE_PADDING = 15;

export default {
    sizeVariants: { small: 280, medium: 420, large: 820 },
    defaultProps: {
        cssOverrides: {
            modalBody: css`
                overflow: hidden;
                border: 1px solid ${color("common.border")};
                border-radius: 0;
                background: ${color("common.ui1")};
                &&&& {
                    box-shadow: inset 0 2px 0 0 ${color("secondary.main")};
                }
                overflow: unset;

                //Delete this media query for a full-screen modal on mobile
                ${xScreen("lte", "md")} {
                    max-width: 90%;
                    height: auto;
                    max-height: calc(100% - 35px);
                    margin: 15px;
                }
            `,
            headlineTypography: css``,
            titleButton: css`
                &&&& {
                    ${buttonTransparent}
                    padding: 0;
                    position: absolute;
                    right: 16px;
                    top: 16px;
                    &:focus {
                        outline: none;
                    }

                    & > ${IconWrapper} {
                        position: unset;
                        top: 0;
                        color: ${color("text.main")};
                    }
                    &:hover:not(:disabled) {
                        ${buttonTransparent}
                        background-color: ${color("common.ui1")};
                        & > ${IconWrapper} {
                            color: ${color("text.main", 0.33)};
                        }
                    }
                }
            `,
            modalTitle: css`
                &&& {
                    border: none;
                    padding: ${MODAL_DESKTOP_PADDING}px ${MODAL_DESKTOP_PADDING}px ${MODAL_DESKTOP_PADDING / 2}px;
                    ${xScreen("lte", "xs")} {
                        padding-left: ${MODAL_MOBILE_PADDING}px;
                        padding-right: ${MODAL_MOBILE_PADDING}px;
                    }
                    & > h4 {
                        padding-right: 0;
                        margin-bottom: 0;
                        ${ellipsis(1)}
                    }
                }
            `,
            modalContent: css`
                flex: 1;
                display: flex;
                flex-direction: column;

                ${styledScrollbar(4)}

                .modal-actions {
                    display: flex;
                    flex: 0 0 auto;
                    flex-wrap: wrap;
                    justify-content: flex-end;
                    width: 100%;
                    margin-top: 20px;

                    background: ${color("common.ui1")};

                    button {
                        margin: 0 0 10px 0;
                    }
                }

                ${xScreen("lte", "xs")} {
                    padding-left: ${MODAL_MOBILE_PADDING}px;
                    padding-right: ${MODAL_MOBILE_PADDING}px;

                    .modal-actions {
                        flex-direction: column;
                        justify-content: center;

                        button {
                            margin: 10px 0 0 0;
                            width: 100%;

                            &:first-child {
                                margin-top: 0;
                            }
                        }
                    }
                }
            `,
        },
        sizeVariant: "medium",
        headlineTypographyProps: {
            variant: "h4",
            css: css`
                margin-bottom: 0;
            `,
        },
        closeButtonProps: {
            buttonType: "solid",
            sizeVariant: "small",
            shape: "pill",
            variant: "primary",
            color: "common.ui1",
            size: 42,
        },
        closeButtonIconProps: {
            src: RemoveAll,
            size: 10,
            color: "text.ui6",
        },
    },
} as xBaseTheme["modal"];
